// Tremor Table [v1.0.0]

import { IconChevronDown, IconChevronUp, IconSelector, IconChevronLeft, IconChevronRight } from '@/components/icons'
import { cx } from '@/lib/utils'
import { Skeleton } from '@/components/Skeleton'

export function Table({
  children,
  className,
  stickyHeader,
  maxHeight = '70vh',
  pagination,
}: {
  children: React.ReactNode
  className?: string
  /** Bounds the table body to `maxHeight` and scrolls internally so a sticky <TableHead sticky> stays put. */
  stickyHeader?: boolean
  /** CSS max-height for the scrolling body when stickyHeader is set, e.g. 'calc(100vh - 340px)'. */
  maxHeight?: string
  /** Rendered as a footer sibling below the scrolling table, e.g. <TablePagination />. */
  pagination?: React.ReactNode
}) {
  return (
    <div
      className={cx(
        'flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm',
        className,
      )}
    >
      <div
        className="overflow-auto flex-1"
        style={stickyHeader ? { maxHeight, minHeight: maxHeight === '100%' ? 0 : maxHeight } : undefined}
      >
        <table tremor-id="tremor-raw" className="min-w-full caption-bottom">
          {children}
        </table>
      </div>
      {pagination}
    </div>
  )
}

export function TableHead({ children, sticky }: { children: React.ReactNode; sticky?: boolean }) {
  return (
    <thead className={cx('bg-[#fafafa]', sticky && 'sticky top-0 z-10')}>
      <tr>{children}</tr>
    </thead>
  )
}

interface TableHeaderCellProps {
  children: React.ReactNode
  onClick?: () => void
  sortable?: boolean
  sorted?: 'asc' | 'desc' | false
  className?: string
  align?: 'left' | 'right' | 'center'
}

export function TableHeaderCell({
  children,
  onClick,
  sortable,
  sorted,
  className,
  align = 'left',
}: TableHeaderCellProps) {
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
  const justifyClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'
  return (
    <th
      onClick={onClick}
      className={cx(
        'whitespace-nowrap border-b border-gray-200 px-3 py-2.5 text-xs font-semibold text-gray-900',
        alignClass,
        sortable && 'cursor-pointer select-none hover:text-gray-600',
        className,
      )}
    >
      {sortable ? (
        <span className={cx('flex items-center gap-1', justifyClass)}>
          {children}
          {sorted === 'asc' ? (
            <IconChevronUp className="h-3.5 w-3.5" />
          ) : sorted === 'desc' ? (
            <IconChevronDown className="h-3.5 w-3.5" />
          ) : (
            <IconSelector className="h-3.5 w-3.5 text-gray-300" />
          )}
        </span>
      ) : (
        children
      )}
    </th>
  )
}

export function TableBody({ children }: { children: React.ReactNode }) {
  return <tbody className="divide-y divide-gray-200">{children}</tbody>
}

export function TableRow({
  children,
  className,
  onClick,
}: {
  children: React.ReactNode
  className?: string
  onClick?: () => void
}) {
  return (
    <tr onClick={onClick} className={cx('hover:bg-gray-50', onClick && 'cursor-pointer', className)}>
      {children}
    </tr>
  )
}

export function TableCell({
  children,
  className,
  colSpan,
  align,
}: {
  children: React.ReactNode
  className?: string
  colSpan?: number
  align?: 'left' | 'right' | 'center'
}) {
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
  return (
    <td className={cx('px-3 py-2 text-xs', alignClass, className)} colSpan={colSpan}>
      {children}
    </td>
  )
}

const AVATAR_SKELETON_SIZE_CLASSES = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
}

/** Placeholder rows for a table's loading state — swap in for the real `.map(...)` rows so
 * the header (and its column widths) stay put instead of the whole table collapsing to a
 * centered spinner. */
export function TableRowsSkeleton({
  rows,
  columns,
  avatarColumn,
  avatarSize = 'sm',
  columnAligns,
}: {
  rows: number
  columns: number
  /** 0-indexed column that normally shows a UserAvatar + name — rendered as a circle + bar
   * instead of a plain bar, matching that avatar's real size. */
  avatarColumn?: number
  avatarSize?: keyof typeof AVATAR_SKELETON_SIZE_CLASSES
  /** 0-indexed alignment per column, matching each TableHeaderCell's own `align` — a full-width
   * bar reads as "left" regardless of text-align, so centered/right columns get a short bar
   * nudged to the matching side instead, to actually sit under their header. */
  columnAligns?: ('left' | 'center' | 'right')[]
}) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <TableRow key={i}>
          {Array.from({ length: columns }, (_, j) => {
            if (j === avatarColumn) {
              return (
                <TableCell key={j} className="py-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className={cx('shrink-0 rounded-full', AVATAR_SKELETON_SIZE_CLASSES[avatarSize])} />
                    <Skeleton className="h-4 w-full max-w-32" />
                  </div>
                </TableCell>
              )
            }
            const align = columnAligns?.[j] ?? 'left'
            return (
              <TableCell key={j} className="py-3">
                <Skeleton
                  className={cx(
                    'h-5',
                    align === 'left' ? 'w-full max-w-32' : 'w-12',
                    align === 'right' && 'ml-auto',
                    align === 'center' && 'mx-auto',
                  )}
                />
              </TableCell>
            )
          })}
        </TableRow>
      ))}
    </>
  )
}

export function TableEmpty({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-10 text-center text-xs text-gray-500">
        {children}
      </td>
    </tr>
  )
}

interface TablePaginationProps {
  /** 0-indexed current page. */
  page: number
  pageSize: number
  totalCount: number
  /** Called with the 0-indexed page to navigate to. */
  onPageChange: (page: number) => void
}

/** First/last two pages plus a window around `current`, with 'ellipsis' filling any gaps. All 1-indexed. */
function getPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = new Set<number>([1, 2, total - 1, total, current - 1, current, current + 1])
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b)
  const result: (number | 'ellipsis')[] = []
  let prev = 0
  for (const p of sorted) {
    if (prev && p - prev > 1) result.push('ellipsis')
    result.push(p)
    prev = p
  }
  return result
}

const paginationArrowClass =
  'rounded-md p-1.5 text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50'

export function TablePagination({ page, pageSize, totalCount, onPageChange }: TablePaginationProps) {
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const currentPage = page + 1
  const canPrevious = page > 0
  const canNext = currentPage < pageCount
  const from = totalCount === 0 ? 0 : page * pageSize + 1
  const to = Math.min((page + 1) * pageSize, totalCount)
  const pageNumbers = getPageNumbers(currentPage, pageCount)

  return (
    <div className="grid w-full flex-shrink-0 grid-cols-[1fr_auto_1fr] items-center border-t border-gray-200 bg-[#fafafa] px-3 py-2">
      <span className="justify-self-start text-xs text-gray-500">
        Showing{' '}
        <span className="font-medium text-gray-900">
          {from}-{to}
        </span>{' '}
        of <span className="font-medium text-gray-900">{totalCount}</span>
      </span>

      <div className="justify-self-center">
        {/* Numbered pagination for wider viewports */}
        <div className="hidden items-center gap-0.5 sm:inline-flex">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={!canPrevious}
            className={paginationArrowClass}
          >
            <span className="sr-only">Previous</span>
            <IconChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          {pageNumbers.map((p, i) =>
            p === 'ellipsis' ? (
              <span key={`ellipsis-${i}`} className="px-1.5 text-xs text-gray-400">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p - 1)}
                aria-current={p === currentPage ? 'page' : undefined}
                className={cx(
                  'min-w-[28px] rounded-md px-2 py-1.5 text-xs font-medium',
                  p === currentPage ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-white',
                )}
              >
                {p}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={!canNext}
            className={paginationArrowClass}
          >
            <span className="sr-only">Next</span>
            <IconChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* Compact Page X of Y for narrow viewports */}
        <div className="flex items-center gap-1 sm:hidden">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={!canPrevious}
            className={paginationArrowClass}
          >
            <span className="sr-only">Previous</span>
            <IconChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="px-1 text-xs text-gray-500">
            Page {currentPage} of {pageCount}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={!canNext}
            className={paginationArrowClass}
          >
            <span className="sr-only">Next</span>
            <IconChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div />
    </div>
  )
}
